import React, { useState, useCallback, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { useS3Audio } from '../hooks/useS3Audio';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge } from '../lib/GameVisuals';
import { Clock, Hash, Headphones, Calculator, ArrowRight, Volume2, Lightbulb } from 'lucide-react';

/* ── helpers (unchanged logic) ── */
function parseTimeDisplay(td) {
  if (!td) return null;
  const clean = td.replace(/\s*(AM|PM)\s*/i, '').trim();
  let parts;
  if (clean.includes('h')) parts = clean.split('h');
  else if (clean.includes(':')) parts = clean.split(':');
  else return null;
  return { hour: (parseInt(parts[0]) || 0) % 12, minute: parseInt(parts[1]) || 0 };
}

function clockAngles(timeDisplay, fallbackNumber) {
  const p = parseTimeDisplay(timeDisplay);
  if (p) return { hourAngle: p.hour * 30 + p.minute * 0.5, minuteAngle: p.minute * 6 };
  const n = fallbackNumber || 0;
  return { hourAngle: (n * 30) % 360, minuteAngle: (n * 6) % 360 };
}

function normalizeAnswer(str) {
  return str.toLowerCase().trim().replace(/\.\s*$/, '').replace(/\s+/g, ' ');
}

function checkAnswer(input, item) {
  const norm = normalizeAnswer(input);
  if (!norm) return false;
  const accepted = [];
  const m = item.mode;
  if (m === 'see_produce_french' || m === 'see_clock_produce_french' || m === 'read_set_clock') {
    if (item.french_word) accepted.push(normalizeAnswer(item.french_word));
    if (item.time_french) accepted.push(normalizeAnswer(item.time_french));
  } else if (m === 'hear_type_number') {
    if (item.number != null) accepted.push(String(item.number));
  } else if (m === 'math_in_french') {
    if (item.number != null) accepted.push(String(item.number));
    if (item.french_word) accepted.push(normalizeAnswer(item.french_word));
  } else if (m === 'time_reading') {
    if (item.time_french) accepted.push(normalizeAnswer(item.time_french));
  } else if (m === 'hear_type_time' || m === 'hear_produce_time') {
    if (item.time_display) accepted.push(normalizeAnswer(item.time_display));
    if (item.time_french) accepted.push(normalizeAnswer(item.time_french));
  } else if (m === 'time_with_future') {
    if (item.time_french) accepted.push(normalizeAnswer(item.time_french));
    if (item.time_display) accepted.push(normalizeAnswer(item.time_display));
    if (item.future_activity) accepted.push(normalizeAnswer(item.future_activity));
  } else {
    if (item.french_word) accepted.push(normalizeAnswer(item.french_word));
    if (item.time_french) accepted.push(normalizeAnswer(item.time_french));
    if (item.number != null) accepted.push(String(item.number));
  }
  return accepted.some(a => a === norm || a === norm + '.' || norm === a + '.');
}

function getCorrectAnswer(item) {
  const m = item.mode;
  if (m === 'see_produce_french' || m === 'see_clock_produce_french' || m === 'read_set_clock') return item.french_word || item.time_french;
  if (m === 'hear_type_number' || m === 'math_in_french') return item.number != null ? String(item.number) : item.french_word;
  if (m === 'time_reading') return item.time_french;
  if (m === 'hear_type_time' || m === 'hear_produce_time') return item.time_display || item.time_french;
  if (m === 'time_with_future') return item.time_french || item.time_display;
  return item.french_word || item.time_french || String(item.number ?? '');
}

function getModeInfo(mode) {
  if (mode === 'see_produce_french') return { label: 'Numbers', icon: Hash, color: '#4C6EF5', bg: '#4C6EF512' };
  if (mode === 'hear_type_number' || mode === 'hear_type_time' || mode === 'hear_produce_time') return { label: 'Listening', icon: Headphones, color: '#7950F2', bg: '#7950F212' };
  if (mode === 'math_in_french') return { label: 'French Math', icon: Calculator, color: '#E03131', bg: '#E0313112' };
  return { label: 'Time', icon: Clock, color: '#0CA678', bg: '#0CA67812' };
}

function getInstruction(item) {
  const m = item.mode;
  if (m === 'see_produce_french') return item.number != null ? `Write ${item.number} in French!` : 'Write this in French!';
  if (m === 'hear_type_number') return 'Listen! Type the number';
  if (m === 'math_in_french') return 'Read the French equation and solve!';
  if (m === 'see_clock_produce_french' || m === 'time_reading') return 'Read the clock! Write the time in French';
  if (m === 'hear_produce_time' || m === 'hear_type_time') return 'Listen! Type the time';
  if (m === 'read_set_clock') return 'Read the French, what time is it?';
  if (m === 'time_with_future') return 'What time is mentioned?';
  return 'Type your answer!';
}

function getPromptText(item) {
  const m = item.mode;
  if (m === 'see_produce_french') return item.number != null ? String(item.number) : null;
  if (m === 'math_in_french') return item.math_display || item.math_display_numeric || null;
  if (m === 'read_set_clock') return item.french_word || item.time_french || null;
  if (m === 'time_with_future') return item.sentence_fr || null;
  return null;
}

function isNumPadMode(mode) {
  return mode === 'hear_type_number' || mode === 'math_in_french';
}

function hasAudio(item) {
  return ['hear_type_number', 'hear_type_time', 'hear_produce_time'].includes(item.mode) ? item.audio_text_fr : null;
}

function isTimeMode(mode) {
  return ['see_clock_produce_french', 'time_reading', 'hear_produce_time', 'hear_type_time', 'read_set_clock', 'time_with_future'].includes(mode);
}

/* ── Hint System ── */
function generateHint(answer, level) {
  if (!answer) return null;
  const chars = answer.split('');
  if (level === 1) {
    // Show first letter of each word, rest as underscores
    let inWord = false;
    return chars.map((c, i) => {
      if (c === ' ' || c === '-' || c === '\'') { inWord = false; return c; }
      if (!inWord) { inWord = true; return c; }
      return '_';
    }).join('');
  }
  if (level === 2) {
    // Show first + last letter of each word, every other letter
    const words = answer.split(/(\s+|-)/);
    return words.map(w => {
      if (w.match(/^[\s-]$/)) return w;
      if (w.length <= 2) return w;
      return w.split('').map((c, i) => {
        if (i === 0 || i === w.length - 1 || i % 2 === 0) return c;
        return '_';
      }).join('');
    }).join('');
  }
  // Level 3: almost all revealed, just 1-2 missing
  return chars.map((c, i) => {
    if (c === ' ' || c === '-' || c === '\'' || c === '.') return c;
    if (i % 5 === 3) return '_';
    return c;
  }).join('');
}

/* ── Big Readable Clock for Time Modes ── */
function ReadableClock({ size = 180, hourAngle, minuteAngle }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200">
      <defs>
        <radialGradient id="rc-face"><stop offset="0%" stopColor="#FFF9DB" /><stop offset="100%" stopColor="#FFE8A3" /></radialGradient>
      </defs>
      <circle cx="100" cy="100" r="96" fill="#4C6EF5" />
      <circle cx="100" cy="100" r="90" fill="url(#rc-face)" />
      {/* Hour marks */}
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        return <line key={i} x1={100 + 72 * Math.cos(a)} y1={100 + 72 * Math.sin(a)} x2={100 + 84 * Math.cos(a)} y2={100 + 84 * Math.sin(a)}
          stroke="#333" strokeWidth={i % 3 === 0 ? 4 : 2} strokeLinecap="round" />;
      })}
      {/* Numbers */}
      {[...Array(12)].map((_, i) => {
        const num = i === 0 ? 12 : i;
        const a = (i * 30 - 90) * Math.PI / 180;
        return <text key={i} x={100 + 60 * Math.cos(a)} y={100 + 60 * Math.sin(a) + 6} textAnchor="middle"
          fontSize="16" fontWeight="700" fill="#333" fontFamily="Fredoka">{num}</text>;
      })}
      {/* Minute hand */}
      <line x1="100" y1="100" x2={100 + 58 * Math.cos((minuteAngle - 90) * Math.PI / 180)} y2={100 + 58 * Math.sin((minuteAngle - 90) * Math.PI / 180)}
        stroke="#333" strokeWidth="4" strokeLinecap="round" />
      {/* Hour hand */}
      <line x1="100" y1="100" x2={100 + 40 * Math.cos((hourAngle - 90) * Math.PI / 180)} y2={100 + 40 * Math.sin((hourAngle - 90) * Math.PI / 180)}
        stroke="#4C6EF5" strokeWidth="5.5" strokeLinecap="round" />
      <circle cx="100" cy="100" r="6" fill="#4C6EF5" stroke="white" strokeWidth="2" />
    </svg>
  );
}

/* ── Clocktower Scene: The Game Visual ── */
function ClockTowerScene({ progress, maxLevels, hourAngle, minuteAngle, mood, streak }) {
  const clockRef = useRef(null);
  const prevProgress = useRef(0);
  const starsRef = useRef(null);
  const uid = useRef(`ct-${Math.random().toString(36).slice(2, 6)}`).current;

  // Animate clock jumping up
  useEffect(() => {
    if (!clockRef.current) return;
    if (progress > prevProgress.current) {
      // Bounce UP animation
      const tl = gsap.timeline();
      tl.to(clockRef.current, { y: -20, scaleX: 0.9, scaleY: 1.15, duration: 0.15, ease: 'power2.out' });
      tl.to(clockRef.current, { y: 0, scaleX: 1, scaleY: 1, duration: 0.4, ease: 'bounce.out' });
    }
    prevProgress.current = progress;
  }, [progress]);

  // Mood-based animations
  useEffect(() => {
    if (!clockRef.current) return;
    if (mood === 'happy') {
      gsap.to(clockRef.current, { rotation: 10, duration: 0.12, yoyo: true, repeat: 3, ease: 'power1.inOut', onComplete: () => gsap.set(clockRef.current, { rotation: 0 }) });
    } else if (mood === 'sad') {
      gsap.fromTo(clockRef.current, { x: -6 }, { x: 6, duration: 0.06, yoyo: true, repeat: 5, ease: 'power1.inOut', onComplete: () => gsap.set(clockRef.current, { x: 0 }) });
    } else if (mood === 'wild') {
      gsap.to(clockRef.current, { rotation: 360, duration: 0.5, ease: 'power2.inOut', onComplete: () => gsap.set(clockRef.current, { rotation: 0 }) });
    }
  }, [mood]);

  // Star pop animation
  useEffect(() => {
    if (!starsRef.current || progress === 0) return;
    const stars = starsRef.current.querySelectorAll('.star-earned');
    const last = stars[stars.length - 1];
    if (last) {
      gsap.fromTo(last, { scale: 0, transformOrigin: 'center center' }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.4)' });
    }
  }, [progress]);

  const levels = maxLevels;
  const towerH = 380;
  const groundY = 440;
  const topY = groundY - towerH;
  // Clock position: smooth percentage of tower height
  const progressPct = levels > 0 ? Math.min(progress / levels, 1) : 0;
  const clockY = groundY - (progressPct * towerH) - 45;
  // Visible markers: cap at 12 so they don't overlap
  const maxMarkers = Math.min(levels, 12);
  const markerStep = levels > 0 ? levels / maxMarkers : 1;

  // Mouth path based on mood
  const mouthPath = mood === 'happy'
    ? 'M-15,8 Q0,22 15,8'
    : mood === 'sad'
    ? 'M-12,15 Q0,5 12,15'
    : 'M-12,10 Q0,18 12,10';

  return (
    <svg viewBox="-20 0 300 500" style={{ width: '100%', height: '100%', maxHeight: '100%' }}>
      <defs>
        <linearGradient id={`${uid}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#87CEEB" />
          <stop offset="100%" stopColor="#E0F0FF" />
        </linearGradient>
        <radialGradient id={`${uid}-face`}>
          <stop offset="0%" stopColor="#FFF9DB" />
          <stop offset="100%" stopColor="#FFE8A3" />
        </radialGradient>
        <filter id={`${uid}-glow`}>
          <feGaussianBlur stdDeviation="3" result="glow" />
          <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Sky */}
      <rect x="-20" width="300" height="500" fill={`url(#${uid}-sky)`} rx="12" />

      {/* Clouds */}
      <ellipse cx="50" cy="30" rx="30" ry="12" fill="white" opacity="0.7">
        <animate attributeName="cx" values="50;65;50" dur="8s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="200" cy="50" rx="25" ry="10" fill="white" opacity="0.5">
        <animate attributeName="cx" values="200;185;200" dur="10s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="120" cy="20" rx="18" ry="7" fill="white" opacity="0.4">
        <animate attributeName="cx" values="120;135;120" dur="12s" repeatCount="indefinite" />
      </ellipse>

      {/* Tower structure */}
      <rect x="110" y={topY - 10} width="40" height={towerH + 20} rx="6" fill="#C9A96E" opacity="0.3" />
      <rect x="115" y={topY - 5} width="30" height={towerH + 10} rx="4" fill="#D4B483" opacity="0.4" />
      {/* Tower center line */}
      <line x1="130" y1={topY} x2="130" y2={groundY} stroke="#B8935A" strokeWidth="3" strokeDasharray="8,6" opacity="0.3" />

      {/* Level markers and earned stars */}
      <g ref={starsRef}>
        {Array.from({ length: maxMarkers }).map((_, i) => {
          const markerLevel = Math.round((i + 1) * markerStep);
          const markerPct = (i + 1) / maxMarkers;
          const ly = groundY - (markerPct * towerH);
          const earned = progress >= markerLevel;
          return (
            <g key={i}>
              {/* Platform */}
              <rect x="112" y={ly + 30} width="36" height="4" rx="2" fill={earned ? '#FFD43B' : '#D4B483'} opacity={earned ? 0.8 : 0.3} />
              {earned ? (
                <g className="star-earned" transform={`translate(130, ${ly + 18})`}>
                  <polygon points="0,-8 2.5,-2.5 8,-2.5 3.5,1.5 5.5,8 0,4 -5.5,8 -3.5,1.5 -8,-2.5 -2.5,-2.5"
                    fill="#FFD43B" stroke="#F59F00" strokeWidth="0.8" />
                </g>
              ) : (
                <circle cx="130" cy={ly + 18} r="4" fill="none" stroke="#B8935A" strokeWidth="1.5" opacity="0.3" />
              )}
            </g>
          );
        })}
      </g>

      {/* Bell/Trophy at very top */}
      <g transform={`translate(130, ${topY - 25})`}>
        <polygon points="0,-14 4,-5 13,-5 6,1 8,10 0,5 -8,10 -6,1 -13,-5 -4,-5"
          fill={progress >= levels ? '#FFD43B' : '#C9A96E'} stroke={progress >= levels ? '#F59F00' : '#B8935A'} strokeWidth="1.5"
          filter={progress >= levels ? `url(#${uid}-glow)` : undefined} opacity={progress >= levels ? 1 : 0.4} />
        {/* Progress label */}
        <text x="0" y="24" textAnchor="middle" fontSize="10" fontWeight="700" fill="#888" fontFamily="Fredoka">
          {progress}/{levels}
        </text>
      </g>

      {/* Ground */}
      <ellipse cx="130" cy={groundY + 10} rx="100" ry="18" fill="#7BC96F" />
      <ellipse cx="130" cy={groundY + 8} rx="70" ry="12" fill="#8FD685" />

      {/* Little flowers on ground */}
      {[45, 80, 175, 210].map((fx, i) => (
        <g key={i} transform={`translate(${fx}, ${groundY + 2})`}>
          <circle cx="0" cy="0" r="3" fill={['#FF6B6B', '#FFD43B', '#69DB7C', '#748FFC'][i]} opacity="0.7" />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#2B8A3E" strokeWidth="1" />
        </g>
      ))}

      {/* ── THE CLOCK CHARACTER ── */}
      <g ref={clockRef} transform={`translate(130, ${clockY})`} style={{ transformOrigin: '130px ' + clockY + 'px' }}>
        {/* Body/legs — little stick legs */}
        <line x1="-10" y1="38" x2="-15" y2="55" stroke="#555" strokeWidth="3" strokeLinecap="round" />
        <line x1="10" y1="38" x2="15" y2="55" stroke="#555" strokeWidth="3" strokeLinecap="round" />
        {/* Arms */}
        <line x1="-35" y1="12" x2="-22" y2={mood === 'happy' ? -8 : 18} stroke="#555" strokeWidth="3" strokeLinecap="round" />
        <line x1="35" y1="12" x2="22" y2={mood === 'happy' ? -8 : 18} stroke="#555" strokeWidth="3" strokeLinecap="round" />
        {/* Glove hands */}
        <circle cx="-35" cy="12" r="5" fill="white" stroke="#333" strokeWidth="1.2" />
        <circle cx="35" cy="12" r="5" fill="white" stroke="#333" strokeWidth="1.2" />
        {/* Shoes */}
        <ellipse cx="-17" cy="57" rx="7" ry="4" fill="#E03131" />
        <ellipse cx="17" cy="57" rx="7" ry="4" fill="#E03131" />

        {/* Clock body — the main circle */}
        <circle cx="0" cy="0" r="36" fill="#4C6EF5" />
        <circle cx="0" cy="0" r="33" fill={`url(#${uid}-face)`} />

        {/* Hour marks */}
        {[...Array(12)].map((_, i) => {
          const a = (i * 30 - 90) * Math.PI / 180;
          return <line key={i} x1={25 * Math.cos(a)} y1={25 * Math.sin(a)} x2={30 * Math.cos(a)} y2={30 * Math.sin(a)}
            stroke="#555" strokeWidth={i % 3 === 0 ? 2.5 : 1.5} strokeLinecap="round" />;
        })}

        {/* Clock numbers (just 12, 3, 6, 9) */}
        {[[0, -20, '12'], [20, 0, '3'], [0, 20, '6'], [-20, 0, '9']].map(([x, y, n]) => (
          <text key={n} x={x} y={y + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#555" fontFamily="Fredoka">{n}</text>
        ))}

        {/* Face — eyes */}
        <ellipse cx="-9" cy="-6" rx="5" ry={mood === 'happy' ? 5 : 5.5} fill="white" stroke="#333" strokeWidth="1.2" />
        <circle cx={mood === 'sad' ? -9 : -8} cy="-6" r="3" fill="#333" />
        <ellipse cx="9" cy="-6" rx="5" ry={mood === 'happy' ? 5 : 5.5} fill="white" stroke="#333" strokeWidth="1.2" />
        <circle cx={mood === 'sad' ? 9 : 10} cy="-6" r="3" fill="#333" />

        {/* Blush on happy */}
        {mood === 'happy' && <>
          <ellipse cx="-16" cy="4" rx="5" ry="3" fill="#FFB3B3" opacity="0.5" />
          <ellipse cx="16" cy="4" rx="5" ry="3" fill="#FFB3B3" opacity="0.5" />
        </>}

        {/* Mouth */}
        <path d={mouthPath} stroke="#E03131" strokeWidth="2.5" fill="none" strokeLinecap="round" />

        {/* Minute hand */}
        <line x1="0" y1="0" x2={20 * Math.cos((minuteAngle - 90) * Math.PI / 180)} y2={20 * Math.sin((minuteAngle - 90) * Math.PI / 180)}
          stroke="#333" strokeWidth="2.5" strokeLinecap="round" />
        {/* Hour hand */}
        <line x1="0" y1="0" x2={14 * Math.cos((hourAngle - 90) * Math.PI / 180)} y2={14 * Math.sin((hourAngle - 90) * Math.PI / 180)}
          stroke="#4C6EF5" strokeWidth="3" strokeLinecap="round" />
        {/* Center dot */}
        <circle cx="0" cy="0" r="3" fill="#4C6EF5" stroke="white" strokeWidth="1" />

        {/* Crown/hat for streaks */}
        {streak >= 3 && (
          <g transform="translate(0, -38)">
            <polygon points="0,-10 -10,0 -6,0 -4,-6 0,-2 4,-6 6,0 10,0" fill="#FFD43B" stroke="#F59F00" strokeWidth="0.8" />
          </g>
        )}
      </g>

      {/* Sparkles on correct */}
      {mood === 'happy' && (
        <g>
          {[[-25, -15], [25, -20], [-15, 20], [20, 15], [0, -30]].map(([dx, dy], i) => (
            <circle key={i} cx={130 + dx} cy={clockY + dy} r="3" fill="#FFD43B" opacity="0.8">
              <animate attributeName="r" values="0;5;0" dur="0.6s" begin={`${i * 0.08}s`} repeatCount="1" fill="freeze" />
              <animate attributeName="opacity" values="0;1;0" dur="0.6s" begin={`${i * 0.08}s`} repeatCount="1" fill="freeze" />
            </circle>
          ))}
        </g>
      )}
    </svg>
  );
}

/* ── Tutorial Screen ── */
function TutorialScreen({ onStart, items }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    gsap.fromTo(ref.current.querySelectorAll('.t-item'), { opacity: 0, y: 20 }, { opacity: 1, y: 0, stagger: 0.1, duration: 0.4, ease: 'power2.out' });
  }, []);

  // Detect which types exist in the actual items
  const typesPresent = new Set();
  (items || []).forEach(it => {
    const m = it.mode;
    if (m === 'see_produce_french') typesPresent.add('numbers');
    else if (m === 'math_in_french') typesPresent.add('french_math');
    else if (['hear_type_number', 'hear_type_time', 'hear_produce_time'].includes(m)) typesPresent.add('listening');
    else typesPresent.add('time');
  });

  const allCards = [
    { key: 'numbers', icon: Hash, c: '#4C6EF5', t: 'Numbers', d: '42 → quarante-deux' },
    { key: 'time', icon: Clock, c: '#0CA678', t: 'Tell Time', d: '3:00 → trois heures' },
    { key: 'listening', icon: Headphones, c: '#7950F2', t: 'Listening', d: 'Audio → type answer' },
    { key: 'french_math', icon: Calculator, c: '#E03131', t: 'French Math', d: 'vingt + quinze → trente-cinq' },
  ];
  const visibleCards = allCards.filter(c => typesPresent.has(c.key));
  const gridCols = visibleCards.length === 1 ? '1fr' : '1fr 1fr';

  return (
    <div data-testid="horloge-tutorial" style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #87CEEB 0%, #E0F0FF 50%, #F0F4F8 100%)', padding: '2rem',
    }}>
      <div ref={ref} style={{ maxWidth: 800, width: '100%', textAlign: 'center' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Clock size={72} color="#4C6EF5" />
        </div>
        <h1 style={{ fontFamily: 'Fredoka', fontSize: '3rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.3rem' }}>
          L'Horloge Folle
        </h1>
        <p style={{ fontFamily: 'Fredoka', fontSize: '1.4rem', color: '#666', margin: '0 0 1.5rem', lineHeight: 1.4 }}>
          Help the Crazy Clock climb the tower! Answer questions to make it jump higher.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem', marginBottom: '2rem', textAlign: 'left', maxWidth: visibleCards.length === 1 ? 400 : 800, margin: '0 auto 2rem' }}>
          {visibleCards.map((ex, i) => (
            <div className="t-item" key={i} style={{
              background: 'white', borderRadius: '1rem', padding: '1.25rem 1.5rem',
              border: `2px solid ${ex.c}20`, boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <ex.icon size={24} color={ex.c} />
                <span style={{ fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.3rem', color: ex.c }}>{ex.t}</span>
              </div>
              <div style={{ fontFamily: 'Fredoka', fontSize: '1.15rem', color: '#555', fontWeight: 600 }}>{ex.d}</div>
            </div>
          ))}
        </div>
        <button data-testid="start-game-btn" onClick={onStart} style={{
          padding: '1.1rem 4rem', borderRadius: '1.25rem',
          background: '#4C6EF5', color: 'white', border: 'none', borderBottom: '6px solid #3B5BDB',
          fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Fredoka', cursor: 'pointer',
          boxShadow: '0 6px 24px rgba(76,110,245,0.3)',
          display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
        }}>
          Let's Go! <ArrowRight size={26} />
        </button>
      </div>
    </div>
  );
}

/* ── Main Game ── */
export function HorlogeFolle({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('horloge_folle'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const maxRounds = Math.min(settings.rounds || 999, items.length);
  const [showTutorial, setShowTutorial] = useState(true);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong'
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [showConfetti, setShowConfetti] = useState(0);
  const [streak, setStreak] = useState(0);
  const [clockMood, setClockMood] = useState('idle'); // idle | happy | sad | wild
  const [hintLevel, setHintLevel] = useState(0); // 0 = no hint, 1-3 = progressive hints
  const endedRef = useRef(false);
  const inputRef = useRef(null);
  const questionRef = useRef(null);
  const scoreRef = useRef(null);
  const latestScore = useRef(0);
  const latestTotal = useRef(0);
  const latestStreak = useRef(0);
  const bestStreakRef = useRef(0);
  const reviewRef = useRef([]);
  const item = items[idx];
  const { play: playS3Audio, stop: stopS3Audio } = useS3Audio();

  // Animate question card entrance
  useEffect(() => {
    if (showTutorial || !questionRef.current) return;
    gsap.fromTo(questionRef.current, { opacity: 0, x: 30 }, { opacity: 1, x: 0, duration: 0.35, ease: 'power2.out' });
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 150);
    setClockMood('idle');
    setHintLevel(0);
  }, [idx, showTutorial]);

  // Auto-play audio for listening modes
  useEffect(() => {
    if (!item || showTutorial) return;
    const audioText = hasAudio(item);
    if (audioText) setTimeout(() => speakFrench(audioText, item), 500);
  }, [idx, showTutorial]);

  const speakFrench = useCallback((text, itemObj) => {
    if (!text) return;
    const id = itemObj?.item_id;
    playS3Audio('horloge_folle', id, text);
  }, [playS3Audio]);

  const handleSubmit = useCallback((answer) => {
    if (feedback || !item || endedRef.current) return;
    const isCorrect = checkAnswer(answer, item);
    const correctAns = getCorrectAnswer(item);
    const promptText = getPromptText(item) || getInstruction(item);
    setTotalAnswered(a => { const n = a + 1; latestTotal.current = n; return n; });
    // Track for review
    reviewRef.current.push({
      prompt: promptText, correctAnswer: correctAns || '—',
      userAnswer: answer, wasCorrect: isCorrect,
      type: getModeInfo(item.mode).label,
    });
    if (isCorrect) {
      playSound('correct');
      setScore(s => { const n = s + 1; latestScore.current = n; return n; });
      setFeedback('correct');
      setClockMood('happy');
      const ns = streak + 1;
      setStreak(ns);
      latestStreak.current = ns;
      if (ns > bestStreakRef.current) bestStreakRef.current = ns;
      if (ns >= 3) setTimeout(() => setClockMood('wild'), 300);
      if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.5 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
    } else {
      playSound('wrong');
      setFeedback('wrong');
      setClockMood('sad');
      setStreak(0);
      latestStreak.current = 0;
    }
    setTimeout(() => {
      setFeedback(null);
      setInput('');
      const next = idx + 1;
      if (next >= maxRounds) endGame();
      else setIdx(next);
    }, 1300);
  }, [feedback, item, idx, maxRounds, playSound, streak]);

  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    stopS3Audio();
    setShowConfetti(c => c + 1);
    setClockMood('wild');
    playSound('win');
    const finalScore = latestScore.current;
    const finalTotal = latestTotal.current;
    const finalStreak = bestStreakRef.current;
    // Add unanswered questions
    const review = [...reviewRef.current];
    for (let i = review.length; i < Math.min(maxRounds, items.length); i++) {
      const it = items[i]; if (!it) break;
      review.push({
        prompt: getPromptText(it) || getInstruction(it),
        correctAnswer: getCorrectAnswer(it) || '—', userAnswer: null, wasCorrect: null,
        type: getModeInfo(it.mode).label,
      });
    }
    setTimeout(() => onGameEnd({
      team1Score: finalScore, team2Score: 0, totalQuestions: finalTotal,
      accuracy: finalTotal > 0 ? (finalScore / finalTotal) * 100 : 0, bestStreak: finalStreak,
      questionReview: review, reviewType: 'qa',
    }), 2000);
  }, [onGameEnd, playSound]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  if (showTutorial) return <TutorialScreen onStart={() => setShowTutorial(false)} items={items} />;
  if (!item) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Fredoka', fontSize: '1.5rem', color: '#888' }}>No content available</div>;

  const angles = clockAngles(item.time_display, item.number);
  const modeInfo = getModeInfo(item.mode);
  const ModeIcon = modeInfo.icon;
  const instruction = getInstruction(item);
  const prompt = getPromptText(item);
  const audioText = hasAudio(item);
  const correctAnswer = feedback === 'wrong' ? getCorrectAnswer(item) : null;
  const numPad = isNumPadMode(item.mode);
  const showBigClock = isTimeMode(item.mode);
  const hintAnswer = getCorrectAnswer(item);
  const currentHint = hintLevel > 0 ? generateHint(hintAnswer, hintLevel) : null;

  return (
    <div data-testid="horloge-folle-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #87CEEB 0%, #C8E6FF 40%, #E8F4FD 70%, #F0F4F8 100%)',
      overflow: 'hidden',
    }}>
      <Confetti trigger={showConfetti} />

      {/* Header */}
      <GameHeader title="L'HORLOGE FOLLE" icon={<Clock size={24} color="var(--accent)" />} color="var(--accent)">
        <ScoreBadge label="SCORE" value={score} color="var(--success)" scoreRef={scoreRef} />
        <ScoreBadge label="ROUND" value={`${idx + 1}/${maxRounds}`} color="var(--accent)" />
        {streak >= 3 && (
          <div style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 700, color: '#E03131', background: 'rgba(224,49,49,0.08)', padding: '0.2rem 0.6rem', borderRadius: '1rem' }}>
            Streak {streak}
          </div>
        )}
      </GameHeader>

      {/* Main game area — Tower left, Question right */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: '2rem', padding: '0.25rem 2rem 0.5rem', minHeight: 0 }}>

        {/* LEFT: Clocktower Scene — the star of the show */}
        <div data-testid="clocktower-scene" style={{
          width: 420, flexShrink: 0,
          display: 'flex', alignItems: 'stretch',
        }}>
          <ClockTowerScene
            progress={score}
            maxLevels={maxRounds}
            hourAngle={angles.hourAngle}
            minuteAngle={angles.minuteAngle}
            mood={clockMood}
            streak={streak}
          />
        </div>

        {/* RIGHT: Question + Input */}
        <div style={{ width: 480, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <div ref={questionRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem', width: '100%', maxWidth: 500 }}>

            {/* Mode badge */}
            <div data-testid="mode-badge" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.25rem 0.75rem', borderRadius: '2rem',
              background: modeInfo.bg, border: `1.5px solid ${modeInfo.color}30`,
            }}>
              <ModeIcon size={14} color={modeInfo.color} />
              <span style={{ fontFamily: 'Fredoka', fontSize: '0.8rem', fontWeight: 700, color: modeInfo.color }}>{modeInfo.label}</span>
            </div>

            {/* Instruction */}
            <div data-testid="instruction-text" style={{
              fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 600, color: '#555',
              textAlign: 'center', lineHeight: 1.3,
            }}>{instruction}</div>

            {/* BIG readable clock — for time modes */}
            {showBigClock && (
              <div data-testid="big-clock-display">
                <ReadableClock size={200} hourAngle={angles.hourAngle} minuteAngle={angles.minuteAngle} />
              </div>
            )}

            {/* Prompt — numbers, math, or text */}
            {prompt && (
              <div data-testid="prompt-display" style={{
                fontFamily: 'Fredoka', fontSize: prompt.length > 30 ? '1.3rem' : '3rem', fontWeight: 700,
                color: '#1a1a2e', background: 'rgba(255,255,255,0.9)',
                padding: prompt.length > 30 ? '0.6rem 1.2rem' : '0.4rem 2rem',
                borderRadius: '1rem', border: `2px solid ${modeInfo.color}20`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                textAlign: 'center', maxWidth: '100%', lineHeight: 1.3,
              }}>{prompt}</div>
            )}

            {/* Audio indicator + replay */}
            {audioText && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'rgba(121,80,242,0.1)', border: '2px solid rgba(121,80,242,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Volume2 size={28} color="#7950F2" />
                </div>
                <button onClick={() => speakFrench(audioText, item)} data-testid="replay-audio-btn"
                  style={{
                    padding: '0.35rem 1rem', borderRadius: '0.75rem',
                    background: '#7950F2', color: 'white', border: 'none', borderBottom: '3px solid #6741D9',
                    fontSize: '0.85rem', fontWeight: 700, fontFamily: 'Fredoka', cursor: 'pointer',
                  }}>Replay</button>
              </div>
            )}

            {/* Hint display */}
            {currentHint && (
              <div data-testid="hint-display" style={{
                fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.15em',
                color: '#7950F2', background: 'rgba(121,80,242,0.06)',
                padding: '0.5rem 1.5rem', borderRadius: '0.75rem',
                border: '1.5px dashed rgba(121,80,242,0.3)',
              }}>{currentHint}</div>
            )}

            {/* Input + GO + Hint button row */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', width: '100%', maxWidth: 400 }}>
              {/* Hint button */}
              {!feedback && hintLevel < 3 && (
                <button data-testid="hint-btn" onClick={() => setHintLevel(h => h + 1)}
                  title={hintLevel === 0 ? 'Get a hint' : 'More hints'}
                  style={{
                    minHeight: 52, padding: '0 0.8rem', borderRadius: '0.85rem',
                    background: hintLevel > 0 ? 'rgba(121,80,242,0.1)' : 'rgba(0,0,0,0.04)',
                    color: '#7950F2', border: '2px solid rgba(121,80,242,0.2)',
                    fontSize: '0.85rem', fontWeight: 700, fontFamily: 'Fredoka', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    transition: 'background 0.2s',
                  }}>
                  <Lightbulb size={16} />
                  {hintLevel === 0 ? 'Hint' : `${hintLevel}/3`}
                </button>
              )}
              <input ref={inputRef} data-testid="horloge-input" value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(input); }}
                placeholder={numPad ? 'Answer...' : 'Type your answer...'}
                disabled={!!feedback} autoFocus
                style={{
                  flex: 1, minHeight: 52, padding: '0.4rem 0.8rem', fontSize: '1.4rem', fontWeight: 700,
                  borderRadius: '0.85rem',
                  border: `2.5px solid ${feedback === 'correct' ? '#40C057' : feedback === 'wrong' ? '#E03131' : 'rgba(0,0,0,0.08)'}`,
                  background: feedback === 'correct' ? 'rgba(64,192,87,0.08)' : feedback === 'wrong' ? 'rgba(224,49,49,0.08)' : 'white',
                  color: '#1a1a2e', textAlign: 'center', outline: 'none', fontFamily: 'Fredoka',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)', transition: 'border-color 0.2s, background 0.2s',
                }} />
              <button data-testid="horloge-go-btn" onClick={() => handleSubmit(input)} disabled={!!feedback || !input}
                style={{
                  minHeight: 52, padding: '0 1.3rem', borderRadius: '0.85rem',
                  background: '#4C6EF5', color: 'white', border: 'none', borderBottom: '4px solid #3B5BDB',
                  fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Fredoka', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(76,110,245,0.2)',
                  opacity: (!input || feedback) ? 0.5 : 1,
                }}>GO</button>
            </div>

            {/* Number pad */}
            {numPad && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem', maxWidth: 300, width: '100%' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(n => (
                  <button key={n} data-testid={`numpad-${n}`} onClick={() => setInput(prev => prev + n)} disabled={!!feedback}
                    style={{
                      minHeight: 46, fontSize: '1.3rem', fontWeight: 700, borderRadius: '0.6rem',
                      border: '2px solid rgba(0,0,0,0.06)', borderBottom: '3px solid rgba(0,0,0,0.08)',
                      background: 'white', color: '#1a1a2e', cursor: 'pointer', fontFamily: 'Fredoka',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                    }}>{n}</button>
                ))}
              </div>
            )}

            {/* Feedback */}
            {feedback === 'correct' && (
              <div data-testid="feedback-correct" style={{
                fontFamily: 'Fredoka', fontSize: '1.05rem', fontWeight: 700, color: '#40C057',
                background: 'rgba(64,192,87,0.1)', padding: '0.35rem 1rem', borderRadius: '0.75rem',
              }}>Correct!</div>
            )}
            {correctAnswer && (
              <div data-testid="feedback-wrong" style={{
                fontFamily: 'Fredoka', fontSize: '1.05rem', fontWeight: 700, color: '#F59F00',
                background: 'rgba(245,159,0,0.1)', padding: '0.35rem 1rem', borderRadius: '0.75rem',
              }}>Answer: <strong>{correctAnswer}</strong></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
